import React, { useContext } from "react";
import { flatten } from "flat";
import { Formik, Form, FieldArray, setNestedObjectValues } from "formik";
import { Context } from "../inc/state";
import { Actions, Radio, FieldWrapper, YesNo, FundName, SelectInput, MaskedInput } from "./fields";
import Accordion from "react-bootstrap/Accordion";
import { normalizeField } from "../inc/functions";
import errorMessages from "../inc/errorMessages";
import countries from "../inc/countries";

import * as Yup from "yup";

import LegalEntity, { LegalEntitySchema } from "./LegalEntity";
import LegalEntityType, { LegalEntityTypeSchema } from "./LegalEntityType";
import { PersonalFields, PersonalFieldsSchema } from "./PersonalFields";
/**
 * @prettier
 */

export default () => {
	const [state, dispatch, setLoading] = useContext(Context);

	const handleSubmit = (values, { setSubmitting }) => {
		dispatch({ type: "ADD_DATA", payload: { values } });
		dispatch({ type: "STEP_FORWARD" });
	};

	const validationSchema = Yup.object().shape({
		maatschap: Yup.object().shape({
			naam: Yup.string().required(errorMessages.required),
			btwNummer: Yup.string().required(errorMessages.required),
			land: Yup.string().required(errorMessages.required),
			straat: Yup.string().required(errorMessages.required),
			huisnummer: Yup.string().required(errorMessages.required),
			postcode: Yup.string().required(errorMessages.required),
			gemeente: Yup.string().required(errorMessages.required),
		}),

		companies: Yup.array().of(
			Yup.object().shape({
				type: Yup.string().required(errorMessages.required),
				//common
				naam: Yup.string().required(errorMessages.required),
				straat: Yup.string().required(errorMessages.required),
				huisnummer: Yup.string().required(errorMessages.required),
				postcode: Yup.string().required(errorMessages.required),
				gemeente: Yup.string().required(errorMessages.required),

				//Rechtspersoon
				voornaam: Yup.string().when("type", {
					is: "Natuurlijk persoon",
					then: Yup.string().required(errorMessages.required),
				}),
				emailadres: Yup.string().when("type", {
					is: "Natuurlijk persoon",
					then: Yup.string().email(errorMessages.email).required(errorMessages.required),
				}),
				rijksregisternummer: Yup.string().when("type", {
					is: "Natuurlijk persoon",
					then: Yup.string()
						.trim()
						.matches(/\d{2}.\d{2}.\d{2}.\d{3}.\d{2}/, "Ongeldig rijksregisternummer")
						.required(errorMessages.required),
				}),
				telefoonnummer: Yup.string().when("type", {
					is: "Natuurlijk persoon",
					then: Yup.string().required(errorMessages.required),
				}),
				geboorteplaats: Yup.string().when("type", {
					is: "Natuurlijk persoon",
					then: Yup.string().required(errorMessages.required),
				}),

				//Rechtspersoon
				rechtsvorm: Yup.string().when("type", {
					is: "Rechtspersoon",
					then: Yup.string().required(errorMessages.required),
				}),
				btwNumber: Yup.string().when("type", {
					is: "Rechtspersoon",
					then: Yup.string().required(errorMessages.required),
				}),
				legalRepresentatives: Yup.array().when("type", {
					is: "Rechtspersoon",
					then: Yup.array().of(
						Yup.object()
							.shape()
							.concat(LegalEntityTypeSchema)
							.concat(PersonalFieldsSchema)
					),
				}),
			})
		),
	});

	const initialValues = state.values;

	const errorCount = (errors) => {
		let arr = [];

		if ("companies" in errors) {
			const flat = Object.keys(flatten(errors.companies));
			flat.forEach((element) => {
				const key = element.split(".");
				if (key.length > 1) {
					if (!arr[key[0]]) {
						arr[key[0]] = 0;
					}
					arr[key[0]]++;
				}
			});
		}

		return arr;
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initialValues}
			onSubmit={handleSubmit}
		>
			{({ values, errors, validateForm, setTouched, setFieldValue }) => {
				const { companies } = values,
					errorCounts = errorCount(errors),
					isSingle = companies.length < 2,
					isBelgium = values.maatschap.land === "België";

				const fields = [
					{
						name: "maatschap.naam",
						label: "Naam van de maatschap",
						required: true,
					},
					{
						name: "maatschap.btwNummer",
						label:
							"Het BTW nummer van het land waar de maatschap gevestigd is, voorafgegaan door de landcode, bijvoorbeeld BE",
						required: true,
					},
					{
						name: "maatschap.land",
						label: "Land waar de maatschap gevestigd is",
						type: "country",
						required: true,
					},
					{
						name: "maatschap.straat",
						label: "Straat van de maatschap",
						type: "maskedInput",
						required: true,
						mask: isBelgium ? /^[a-zA-Z\s]*$/ : String,
					},
					{
						name: "maatschap.huisnummer",
						label: "Huisnummer van de maatschap",
						type: "maskedInput",
						required: true,
						mask: isBelgium ? Number : String,
					},
					{
						name: "maatschap.bus",
						label: "Bus van de maatschap",
						required: false,
					},
					{
						name: "maatschap.postcode",
						label: "Postcode van de maatschap",
						type: "maskedInput",
						required: true,
						mask: isBelgium ? Number : String,
					},
					{
						name: "maatschap.gemeente",
						label: "Gemeente of stad van de maatschap",
						type: "maskedInput",
						required: true,
						mask: isBelgium ? /^[a-zA-Z\s]*$/ : String,
					},
				].map(normalizeField);

				return (
					<Form noValidate>
						<FundName title={state.fundTitle} subtitle={"Integra III Privak"} />

						{fields.map((field, index) => (
							<React.Fragment key={`maatschap.${index}`}>
								{field.type === "maskedInput" && (
									<MaskedInput
										mask={field.mask}
										id={`${field.id}`}
										name={`${field.name}`}
										text={field.text}
										label={field.label}
										required={field.required}
									/>
								)}

								{field.type.match(/text|email|number/g) && (
									<FieldWrapper
										id={`${field.id}`}
										name={`${field.name}`}
										type={field.type}
										text={field.text}
										label={field.label}
										required={field.required}
									/>
								)}

								{field.type === "country" && (
									<SelectInput
										id={`${field.id}`}
										name={`${field.name}`}
										placeholder="Selecteer land"
										options={countries}
										label={field.label}
										required={field.required}
									/>
								)}
							</React.Fragment>
						))}

						<FieldArray name="companies">
							<Accordion
								defaultActiveKey={[0]}
								flush
								className={isSingle ? "single" : ""}
							>
								{companies.length > 0 &&
									companies.map((company, index) => {
										return (
											<Accordion.Item
												eventKey={index}
												key={`companies.${index}`}
											>
												<Accordion.Header
													onClick={async () => {
														const e = await validateForm();
														setTouched(setNestedObjectValues(e, true));
													}}
												>
													Informatie maat
													{companies.length > 1 && ` ${index + 1}`}
													{errorCounts && errorCounts[index] && (
														<div className="accordion-error">
															<span className="badge rounded-pill text-bg-light">
																{errorCounts[index]}
															</span>
														</div>
													)}
												</Accordion.Header>
												<Accordion.Body>
													<label
														htmlFor={`companies.${index}.type.0`}
														className="mb-3"
													>
														Hoedanigheid
													</label>

													<div className="mb-3">
														{[
															"Rechtspersoon",
															"Natuurlijk persoon",
														].map((field, i) => (
															<Radio
																name={`companies.${index}.type`}
																id={`companies.${index}.type.${i}`}
																key={`companies.${index}.type.${i}`}
																value={field}
																label={`${field}`}
																required={true}
															/>
														))}
													</div>

													{companies[index].type === "Rechtspersoon" && (
														<>
															<LegalEntity
																group={`companies.${index}.`}
																isBelgium={
																	companies[index].land ===
																	"België"
																}
															/>

															<FieldArray
																name={`companies.${index}.legalRepresentatives`}
															>
																<>
																	<h5>
																		Gegevens van de bestuurder
																	</h5>
																	<LegalEntityType
																		group={`companies.${index}.legalRepresentatives.0.`}
																	/>
																	<PersonalFields
																		group={`companies.${index}.legalRepresentatives.0.`}
																		isBelgium={
																			companies[index]
																				.legalRepresentatives[0]
																				.land === "België"
																		}
																	/>
																	<YesNo
																		label="Is deze maat een UBO van de maatschap?"
																		name={`companies.${index}.legalRepresentatives.0.isUBOMaatschap`}
																	/>
																	<YesNo
																		label="Zal deze persoon ten aanzien van Integra worden aangeduid als exclusief houder van de stemrechten van de aandelen?"
																		name={`companies.${index}.legalRepresentatives.0.maatIsDeExclusieveHouderVanDeStemrechten`}
																	/>
																</>
															</FieldArray>
														</>
													)}

													{companies[index].type ===
														"Natuurlijk persoon" && (
														<PersonalFields
															group={`companies.${index}.`}
															isBelgium={
																companies[index]
																	.legalRepresentatives[0]
																	.land === "België"
															}
														/>
													)}
												</Accordion.Body>
											</Accordion.Item>
										);
									})}
							</Accordion>
						</FieldArray>
						<Actions />
					</Form>
				);
			}}
		</Formik>
	);
};
